/**
 * Modal para crear/editar Persona
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input, Select } from "@/components/ui";
import { DatePickerField } from "@/components/ui/DatePicker";
import EpsSelect from "@/components/forms/EpsSelect";
import { X, Loader2, User } from "lucide-react";
import { useCreatePerson, useUpdatePerson } from "@/hooks/usePersons";
import { useAuthStore } from "@/stores/authStore";
import type {
  Person,
  IdDocumentType,
  Gender,
  BloodType,
  MemberType,
} from "@/types";
import { Timestamp } from "firebase/firestore";

const personSchema = z.object({
  idDocumentType: z.enum(["CC", "TI", "RC", "CE", "PASAPORTE", "PPT"]),
  idDocumentNumber: z
    .string()
    .min(5, "El número de documento debe tener al menos 5 caracteres"),
  firstName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  lastName: z.string().min(2, "El apellido debe tener al menos 2 caracteres"),
  birthDate: z.string().min(1, "La fecha de nacimiento es requerida"),
  gender: z.enum(["male", "female"]),
  email: z.string().email("Email inválido").optional().or(z.literal("")),
  phone: z.string().min(1, "El teléfono es requerido"),
  address: z.string().optional(),
  bloodType: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]),
  healthInsurance: z.string().optional(),
  emergencyContactName: z
    .string()
    .min(1, "El nombre del contacto de emergencia es requerido"),
  emergencyContactRelationship: z.string().min(1, "La relación es requerida"),
  emergencyContactPhone: z
    .string()
    .min(1, "El teléfono del contacto de emergencia es requerido"),
  status: z.enum(["active", "inactive"]).default("active"),
});

type PersonForm = z.infer<typeof personSchema>;

interface PersonModalProps {
  isOpen: boolean;
  onClose: () => void;
  person?: Person | null;
}

const documentTypeLabels: Record<string, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  RC: "Registro Civil",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  PPT: "Permiso por Protección Temporal (PPT)",
};

const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

export default function PersonModal({
  isOpen,
  onClose,
  person,
}: PersonModalProps) {
  const { user } = useAuthStore();
  const createMutation = useCreatePerson();
  const updateMutation = useUpdatePerson();
  const isEditing = !!person;

  const formatDateForInput = (
    timestamp: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "object" && "toDate" in timestamp && timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string | number | Date);
    return date.toISOString().split("T")[0];
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PersonForm>({
    resolver: zodResolver(personSchema),
    mode: "onTouched",
    defaultValues: person
      ? {
          idDocumentType: person.idDocumentType,
          idDocumentNumber: person.idDocumentNumber,
          firstName: person.firstName,
          lastName: person.lastName,
          birthDate: formatDateForInput(person.birthDate),
          gender: person.gender,
          email: person.email || "",
          phone: person.phone,
          address: person.address || "",
          bloodType: person.bloodType,
          healthInsurance: person.healthInsurance || "",
          emergencyContactName: person.emergencyContact?.name || "",
          emergencyContactRelationship:
            person.emergencyContact?.relationship || "",
          emergencyContactPhone: person.emergencyContact?.phone || "",
          status: person.status,
        }
      : {
          idDocumentType: "TI",
          status: "active",
          gender: "male",
          bloodType: "O+",
        },
  });

  const watchBirthDate = watch("birthDate");

  const derivedMemberType = useMemo(() => {
    if (!watchBirthDate) return "acampante";
    const age = Math.floor(
      (Date.now() - new Date(watchBirthDate).getTime()) /
        (365.25 * 24 * 60 * 60 * 1000),
    );
    return age >= 16 ? "lider" : "acampante";
  }, [watchBirthDate]);

  const onSubmit = async (data: PersonForm) => {
    if (!user) return;

    const fullName = `${data.firstName} ${data.lastName}`;

    const emergencyContact = {
      name: data.emergencyContactName,
      relationship: data.emergencyContactRelationship,
      phone: data.emergencyContactPhone,
    };

    const personData = {
      idDocumentType: data.idDocumentType as IdDocumentType,
      idDocumentNumber: data.idDocumentNumber,
      firstName: data.firstName,
      lastName: data.lastName,
      fullName,
      birthDate: Timestamp.fromDate(new Date(data.birthDate + "T12:00:00")),
      gender: data.gender as Gender,
      memberType: derivedMemberType as MemberType,
      email: data.email || undefined,
      phone: data.phone,
      address: data.address || undefined,
      bloodType: data.bloodType as BloodType,
      healthInsurance: data.healthInsurance || undefined,
      emergencyContact,
      photoUrl: person?.photoUrl || undefined,
      status: data.status,
    };

    try {
      if (isEditing && person) {
        await updateMutation.mutateAsync({ id: person.id, data: personData });
      } else {
        await createMutation.mutateAsync({
          data: personData as any,
          userId: user.uid,
        });
      }
      reset();
      onClose();
    } catch (error) {
      console.error("Error saving person:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? "Editar Persona" : "Nueva Persona"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* Información de Identificación */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Identificación
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Tipo de Documento *
                </label>
                <Select
                  {...register("idDocumentType")}
                  error={!!errors.idDocumentType}
                >
                  {Object.entries(documentTypeLabels).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
                {errors.idDocumentType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.idDocumentType.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Número de Documento *
                </label>
                <Input
                  {...register("idDocumentNumber")}
                  placeholder="Número de documento"
                  error={!!errors.idDocumentNumber}
                />
                {errors.idDocumentNumber && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.idDocumentNumber.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Información Personal */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Información Personal
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Nombre *
                </label>
                <Input
                  {...register("firstName")}
                  placeholder="Nombre"
                  error={!!errors.firstName}
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Apellido *
                </label>
                <Input
                  {...register("lastName")}
                  placeholder="Apellido"
                  error={!!errors.lastName}
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
              <div>
                <DatePickerField
                  control={control}
                  name="birthDate"
                  label="Fecha de Nacimiento *"
                  error={errors.birthDate?.message as string | undefined}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Género *
                </label>
                <Select {...register("gender")} error={!!errors.gender}>
                  <option value="male">Masculino</option>
                  <option value="female">Femenino</option>
                </Select>
              </div>
            </div>
          </div>

          {/* Información de Contacto */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Contacto</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Teléfono *
                </label>
                <Input
                  {...register("phone")}
                  placeholder="Número de teléfono"
                  error={!!errors.phone}
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.phone.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Email
                </label>
                <Input
                  type="email"
                  {...register("email")}
                  placeholder="correo@ejemplo.com"
                  error={!!errors.email}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.email.message}
                  </p>
                )}
                {!errors.email &&
                  user?.email &&
                  watch("email")?.trim().toLowerCase() ===
                    user.email.toLowerCase() && (
                    <p className="text-amber-600 text-xs mt-1">
                      ⚠️ Este es tu propio correo. Si estás registrando a otra
                      persona (un menor a tu cargo), deja este campo vacío para
                      que las notificaciones lleguen a tu cuenta sin contaminar
                      el perfil.
                    </p>
                  )}
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 mb-1">
                  Dirección
                </label>
                <Input
                  {...register("address")}
                  placeholder="Dirección completa"
                />
              </div>
            </div>
          </div>

          {/* Información Médica */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Información Médica
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Tipo de Sangre *
                </label>
                <Select {...register("bloodType")} error={!!errors.bloodType}>
                  {bloodTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Select>
                {errors.bloodType && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.bloodType.message}
                  </p>
                )}
              </div>
              <div>
                <EpsSelect
                  label="Seguro de Salud"
                  value={watch("healthInsurance") || ""}
                  onChange={(v) =>
                    setValue("healthInsurance", v, { shouldDirty: true })
                  }
                />
              </div>
            </div>
          </div>

          {/* Contacto de Emergencia */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Contacto de Emergencia *
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Nombre *
                </label>
                <Input
                  {...register("emergencyContactName")}
                  placeholder="Nombre completo"
                  error={!!errors.emergencyContactName}
                />
                {errors.emergencyContactName && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactName.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Parentesco *
                </label>
                <Select
                  {...register("emergencyContactRelationship")}
                  error={!!errors.emergencyContactRelationship}
                >
                  <option value="">Seleccionar</option>
                  <option value="Padre">Padre</option>
                  <option value="Madre">Madre</option>
                  <option value="Abuelo/a">Abuelo/a</option>
                  <option value="Tío/a">Tío/a</option>
                  <option value="Hermano/a">Hermano/a</option>
                  <option value="Esposo/a">Esposo/a</option>
                  <option value="Tutor Legal">Tutor Legal</option>
                  <option value="Otro">Otro</option>
                </Select>
                {errors.emergencyContactRelationship && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactRelationship.message}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Teléfono *
                </label>
                <Input
                  {...register("emergencyContactPhone")}
                  placeholder="Teléfono"
                  error={!!errors.emergencyContactPhone}
                />
                {errors.emergencyContactPhone && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.emergencyContactPhone.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-sm text-gray-600 mb-1">Estado</label>
            <Select {...register("status")}>
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </Select>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                isSubmitting ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isSubmitting ||
              createMutation.isPending ||
              updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  {isEditing ? "Guardando..." : "Creando..."}
                </>
              ) : isEditing ? (
                "Guardar Cambios"
              ) : (
                "Crear Persona"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
